// IncomingStock — branch-side queue of HQ Purchase items for this branch.
// v1.3.0 splits the old one-click receive into a two-step workflow:
//   AWAITING_GRN  → branch enters actual qty + clicks "Submit GRN"
//   GRN_SUBMITTED → waiting for HQ to confirm; row shown read-only with
//                   the submitted qty + a "Waiting for HQ" badge.
// Stock only becomes sellable AFTER HQ confirms (separate page).
//
// "current branch" derivation matches StockTransfers: HQ X-Branch when
// on bare keletezm.com, otherwise the host's first subdomain.
import React, { useEffect, useState } from 'react';
import { FiRefreshCw, FiCheckCircle, FiInbox, FiClock, FiXCircle } from 'react-icons/fi';
import {
  isHqHost, getHqBranch, getHqPurchaseIncoming, branchRejectPo, getProducts, getBranchSlug
} from '../services/api';
import ReceiptConfirmationModal from '../components/ReceiptConfirmationModal';

function currentBranchSlug() {
  if (isHqHost()) return getHqBranch() || '';
  // 2026-08-28 — Electron's hostname is always "localhost", so splitting it
  // yielded the slug "localhost" and this page asked for a branch that does
  // not exist: the header read "branch: localhost" and the queue was empty
  // on every till. StockTransfers, CashBook and HQDeposits already had this
  // fallback; IncomingStock was the one that missed it.
  try {
    const host = window.location.hostname;
    if (host === 'localhost' || host === '127.0.0.1') return getBranchSlug() || '';
    return host.split('.')[0];
  } catch { return getBranchSlug() || ''; }
}

const fmtMoney = (n) => `K${parseFloat(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtQty   = (n) => parseFloat(n || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 });

export default function IncomingStock() {
  const slug = currentBranchSlug();
  const [items, setItems] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [rejecting, setRejecting] = useState(null); // { purchase, reason, submitting }
  // v1.10.0 — Accept opens the Receipt Confirmation modal here on the page.
  // Branch confirms received qty + invoice; HQ then generates the GRN.
  const [confirming, setConfirming] = useState(null); // { purchase, items }
  const [toast, setToast] = useState(null);
  // v1.13.91 — 'pending' = live queue (AWAITING_GRN + GRN_SUBMITTED);
  //            'history' = read-only past rows (CONFIRMED / REJECTED / etc).
  const [tab, setTab] = useState('pending');

  useEffect(() => {
    getProducts().then(r => setProducts(r.data || [])).catch(() => {});
  }, []);

  const refresh = async () => {
    if (!slug) return;
    setLoading(true);
    try {
      const r = await getHqPurchaseIncoming(slug, tab);
      setItems(r.data?.items || []);
    } catch (err) {
      flash(err?.response?.data?.error || 'Failed to load', 'error');
    }
    setLoading(false);
  };

  const flash = (text, type) => {
    setToast({ text, type });
    setTimeout(() => setToast(null), type === 'error' ? 4500 : 2500);
  };

  useEffect(() => {
    if (!slug) return;
    refresh();
    // Polling only for the live queue; history doesn't change every 15s.
    if (tab !== 'pending') return;
    const id = setInterval(refresh, 15_000);
    return () => clearInterval(id);
  // eslint-disable-next-line
  }, [slug, tab]);

  // v1.10.0 — Accept opens the Receipt Confirmation modal. Branch confirms
  // received qty + invoice; the actual GRN is generated by HQ via the new
  // /api/hq/grns/generate endpoint. The legacy "navigate to GRN form" flow
  // is gone.
  const openConfirmModal = (group) => {
    setConfirming({
      purchase: {
        purchase_number: group.purchase_number,
        sync_id:         group.purchase_sync_id,
        supplier_name:   group.supplier_name,
        date:            group.purchase_date,
      },
      items: group.items.filter(i => i.status === 'AWAITING_GRN' || i.status === 'AWAITING_CONFIRMATION' || i.status === 'RECEIPT_REPORTED'),
    });
  };

  const submitReject = async () => {
    if (!rejecting) return;
    const reason = (rejecting.reason || '').trim();
    if (!reason) { flash('A rejection reason is required', 'error'); return; }
    setRejecting(r => ({ ...r, submitting: true }));
    try {
      const { data } = await branchRejectPo(rejecting.purchase.purchase_sync_id, reason, slug);
      flash(`Delivery rejected (${data?.rejected || 0} line(s)). HQ has been notified.`, 'success');
      setRejecting(null);
      refresh();
      window.dispatchEvent(new Event('stock:refresh'));
    } catch (err) {
      flash(err?.response?.data?.error || 'Reject failed', 'error');
      setRejecting(r => ({ ...r, submitting: false }));
    }
  };

  if (!slug) {
    return (
      <div className="page-content">
        <div className="page-header"><div><h1>Incoming Stock</h1></div></div>
        <p style={{ color: '#b91c1c' }}>No branch selected. Pick a branch in the HQ sidebar first.</p>
      </div>
    );
  }

  return (
    <div className="page-content">
      <div className="page-header">
        <div>
          <h1>Incoming Stock</h1>
          <p>Submit GRN for what physically arrived. Stock becomes sellable only after HQ confirms · branch: <strong>{slug}</strong></p>
        </div>
        <button onClick={refresh}
          style={{ padding: '8px 14px', background: '#0ea5e9', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <FiRefreshCw /> Refresh
        </button>
      </div>

      {toast && (
        <div style={{
          position: 'fixed', top: 80, right: 20, zIndex: 999,
          padding: '12px 18px', borderRadius: 8, color: '#fff', fontWeight: 600,
          background: toast.type === 'error' ? '#dc2626' : '#16a34a',
        }}>{toast.text}</div>
      )}

      {/* v1.13.91 — Pending / History tabs. Pending = live queue polled
          every 15s; History = read-only past rows (confirmed / rejected).
          Same PO-grouped card layout for both. */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 14 }}>
        {[
          { id: 'pending', label: 'Pending' },
          { id: 'history', label: 'History' },
        ].map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            style={{
              padding: '6px 16px', borderRadius: 16, border: '1px solid',
              borderColor: tab === t.id ? '#0ea5e9' : '#e2e8f0',
              background:  tab === t.id ? '#0ea5e9' : '#fff',
              color:       tab === t.id ? '#fff'    : '#475569',
              cursor: 'pointer', fontSize: 12, fontWeight: 600,
            }}>
            {t.label}
          </button>
        ))}
      </div>

      {/* v1.9.19 — PO-grouped layout. One card per PO with supplier +
          purchase # in the header; items listed underneath. Accept &
          Generate GRN is at the PO header (one PO = one branch = one
          GRN). Reject is still per-line because a single bad item
          shouldn't force the whole PO to be rejected. */}
      <div>
        {loading && items.length === 0 ? (
          <p style={{ color: '#64748b' }}>Loading…</p>
        ) : items.length === 0 ? (
          <p style={{ color: '#94a3b8', fontStyle: 'italic' }}><FiInbox /> No pending incoming stock.</p>
        ) : (
          (() => {
            const groups = [];
            const byPo = new Map();
            for (const it of items) {
              const key = it.purchase_sync_id || it.purchase_number || `i${it.id}`;
              if (!byPo.has(key)) {
                const g = {
                  key,
                  purchase_number: it.purchase_number,
                  purchase_date:   it.purchase_date,
                  supplier_name:   it.supplier_name,
                  supplier_id:     it.supplier_id,
                  purchase_sync_id: it.purchase_sync_id,
                  items: [],
                };
                byPo.set(key, g);
                groups.push(g);
              }
              byPo.get(key).items.push(it);
            }

            return groups.map(g => {
              const firstAwaiting = g.items.find(i => i.status === 'AWAITING_GRN');
              const anyAwaiting   = !!firstAwaiting;
              // 2026-08-29 — "Waiting HQ" must cover the status Kelete actually
              // writes. Kelete's branch action produces GRN_SUBMITTED; Kelete's
              // Confirm Received produces RECEIPT_REPORTED, so this badge — the
              // one telling the branch "we have done our bit, HQ has not" —
              // never appeared on the very screen it was built for.
              const WAITING_HQ = ['GRN_SUBMITTED', 'RECEIPT_REPORTED'];
              const allSubmitted  = g.items.every(i => WAITING_HQ.includes(i.status));
              // v1.9.21 — when a GRN has been submitted, surface its
              // number on the card so the branch user knows exactly
              // which GRN to chase HQ about.
              const submittedGrnNumber = g.items.find(i => i.linked_grn_number)?.linked_grn_number || null;
              // v1.13.36 — PO subtotal from the HQ-supplied cost_price on
              // each line. Displayed in the header and per-line so the
              // branch sees exactly what it's about to owe.
              const poSubtotal = g.items.reduce(
                (s, it) => s + (parseFloat(it.dispatched_qty) || 0) * (parseFloat(it.cost_price) || 0),
                0
              );
              return (
                <div key={g.key} style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: 0, marginBottom: 14, overflow: 'hidden' }}>
                  {/* PO header */}
                  <div style={{ padding: '12px 16px', background: '#f8fafc', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
                    <div>
                      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
                        <span style={{ fontFamily: 'monospace', fontWeight: 700, fontSize: 14, color: '#0f172a' }}>{g.purchase_number}</span>
                        <span style={{ color: '#cbd5e1' }}>·</span>
                        <span style={{ fontSize: 14, fontWeight: 600, color: '#0f172a' }}>{g.supplier_name || '—'}</span>
                        <span style={{ color: '#cbd5e1' }}>·</span>
                        <span style={{ fontSize: 12, color: '#64748b' }}>{g.purchase_date}</span>
                      </div>
                      <div style={{ marginTop: 4, fontSize: 11, color: '#64748b' }}>
                        {g.items.length} item{g.items.length === 1 ? '' : 's'}
                        <span style={{ marginLeft: 8, color: '#0f172a', fontWeight: 700 }}>
                          · PO total {fmtMoney(poSubtotal)}
                        </span>
                        {allSubmitted && <span style={{ ...tag('#fef3c7', '#92400e'), marginLeft: 8 }}><FiClock size={10} style={{ verticalAlign: 'middle' }} /> Waiting HQ</span>}
                        {submittedGrnNumber && (
                          <span style={{ marginLeft: 8, fontFamily: 'monospace', color: '#475569' }}>
                            · GRN <strong>{submittedGrnNumber}</strong>
                          </span>
                        )}
                        {/* 2026-09-04 — who raised it at HQ and who confirmed
                            it. Both names were already in the API response
                            (p.created_by_name AS hq_user, and confirmed_by_name
                            per line) and simply were not shown, so a depot could
                            not tell whose delivery this was or who signed it
                            off. Confirmed-by is read off the first line that
                            has one — HQ confirms a purchase in one action. */}
                        {g.hq_user && (
                          <span style={{ marginLeft: 8, color: '#64748b' }}>
                            · Raised by <strong style={{ color: '#0f172a' }}>{g.hq_user}</strong>
                          </span>
                        )}
                        {(() => {
                          const conf = g.items.find(i => i.confirmed_by_name)?.confirmed_by_name;
                          return conf ? (
                            <span style={{ marginLeft: 8, color: '#64748b' }}>
                              · Confirmed by <strong style={{ color: '#0f172a' }}>{conf}</strong>
                            </span>
                          ) : null;
                        })()}
                      </div>
                    </div>
                    {anyAwaiting && (
                      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                        {/* 2026-08-31 — reject is per DELIVERY, not per line.
                            A truck arrives or it does not; declining one
                            product and confirming the rest left a PO
                            half-rejected against an invoice covering all of
                            it. Part of a delivery being wrong is a short
                            receipt or a credit note, both of which already
                            exist on the Confirm Received screen. */}
                        <button onClick={() => setRejecting({ purchase: g, reason: '', submitting: false })}
                          title="Reject this whole delivery"
                          style={{ padding: '8px 14px', background: '#fff', color: '#b91c1c', border: '1px solid #fecaca', borderRadius: 6, cursor: 'pointer', fontSize: 13, fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                          <FiXCircle size={14} /> Reject Delivery
                        </button>
                        <button onClick={() => openConfirmModal(g)}
                          style={{ padding: '8px 14px', background: '#16a34a', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 13, fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                          <FiCheckCircle size={14} /> Confirm Received
                        </button>
                      </div>
                    )}
                  </div>
                  {/* Items list */}
                  <table className="phone-cards" style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                    <thead style={{ background: '#fff' }}>
                      <tr>
                        <th style={{ ...th, width: 36, textAlign: 'right' }}>#</th>
                        <th style={th}>Product</th>
                        <th style={{ ...th, textAlign: 'right', width: 90 }}>Expected</th>
                        <th style={{ ...th, textAlign: 'right', width: 100 }}>Cost</th>
                        <th style={{ ...th, textAlign: 'right', width: 110 }}>Line Total</th>
                        <th style={{ ...th, textAlign: 'right', width: 90 }}>Submitted</th>
                        <th style={{ ...th, width: 140 }}>Status</th>
                        <th style={{ ...th, width: 100 }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {g.items.map((it, idx) => {
                        const isAwaiting  = it.status === 'AWAITING_GRN';
                        const isSubmitted = WAITING_HQ.includes(it.status);
                        const cost = parseFloat(it.cost_price) || 0;
                        const qty  = parseFloat(it.dispatched_qty) || 0;
                        return (
                          <tr key={it.id} style={{ borderTop: '1px solid #f1f5f9', background: isSubmitted ? '#fffbeb' : '#fff' }}>
                            <td style={{ ...td, textAlign: 'right', color: '#94a3b8', fontSize: 12 }}>{idx + 1}</td>
                            <td style={td}>{it.product_name} <span style={{ color: '#94a3b8', fontSize: 11 }}>{it.unit}</span></td>
                            <td style={{ ...td, textAlign: 'right' }}>{fmtQty(it.dispatched_qty)}</td>
                            <td style={{ ...td, textAlign: 'right', color: '#475569' }}>{fmtMoney(cost)}</td>
                            <td style={{ ...td, textAlign: 'right', fontWeight: 700, color: '#0f172a' }}>{fmtMoney(qty * cost)}</td>
                            <td style={{ ...td, textAlign: 'right', fontWeight: 700 }}>
                              {it.received_qty == null ? '—' : fmtQty(it.received_qty)}
                            </td>
                            <td style={td}>
                              {isAwaiting && <span style={tag('#dbeafe', '#1e40af')}>Awaiting GRN</span>}
                              {isSubmitted && <span style={tag('#fef3c7', '#92400e')}><FiClock size={10} style={{ verticalAlign: 'middle' }} /> Waiting HQ</span>}
                              {/* v1.13.91 — history-tab statuses */}
                              {it.status === 'CONFIRMED' && <span style={tag('#dcfce7', '#166534')}>Confirmed</span>}
                              {it.status === 'REJECTED'  && <span style={tag('#fee2e2', '#991b1b')}>Rejected</span>}
                              {it.status === 'CANCELLED' && <span style={tag('#f3f4f6', '#374151')}>Cancelled</span>}
                              {!isAwaiting && !isSubmitted && !['CONFIRMED','REJECTED','CANCELLED'].includes(it.status) && (
                                <span style={tag('#f3f4f6', '#374151')}>{it.status}</span>
                              )}
                            </td>
                            <td style={td}>

                              {isSubmitted && it.variance_notes && (
                                <span style={{ fontSize: 11, color: '#92400e', fontStyle: 'italic' }}>
                                  {it.variance_notes}
                                </span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                    <tfoot>
                      <tr style={{ background: '#f8fafc', borderTop: '2px solid #e5e7eb' }}>
                        <td colSpan={4} style={{ ...td, textAlign: 'right', fontWeight: 700, color: '#475569', textTransform: 'uppercase', fontSize: 11, letterSpacing: 0.4 }}>PO total</td>
                        <td style={{ ...td, textAlign: 'right', fontWeight: 800, color: '#0f172a' }}>{fmtMoney(poSubtotal)}</td>
                        <td colSpan={3} />
                      </tr>
                    </tfoot>
                  </table>
                </div>
              );
            });
          })()
        )}
      </div>

      {rejecting && (
        <RejectModal
          state={rejecting}
          setState={setRejecting}
          onCancel={() => setRejecting(null)}
          onSubmit={submitReject}
        />
      )}

      {confirming && (
        <ReceiptConfirmationModal
          purchase={confirming.purchase}
          items={confirming.items}
          products={products}
          branchSlug={slug}
          onClose={() => setConfirming(null)}
          onConfirmed={() => {
            setConfirming(null);
            flash('Receipt submitted. HQ will generate the GRN.', 'success');
            refresh();
          }}
        />
      )}
    </div>
  );
}

// v1.9.7 — reason capture for branch-reject. Reason is mandatory; HQ
// sees it on the HQ Purchases list so they can re-route or cancel.
function RejectModal({ state, setState, onCancel, onSubmit }) {
  const { purchase, reason, submitting } = state;
  const lines = (purchase?.items || []).filter(i => i.status === 'AWAITING_GRN');
  return (
    <div style={overlay}>
      <div style={{ background: '#fff', borderRadius: 12, width: 'min(460px, 92vw)', padding: 22 }}>
        <h3 style={{ margin: '0 0 6px 0', color: '#b91c1c' }}>Reject Delivery</h3>
        <p style={{ margin: 0, fontSize: 13, color: '#64748b' }}>
          Purchase <strong>{purchase?.purchase_number}</strong> from {purchase?.supplier_name || 'supplier'}
          {lines.length > 0 && <> · {lines.length} line{lines.length === 1 ? '' : 's'}</>}.
        </p>
        {lines.length > 0 && (
          <ul style={{ margin: '8px 0 0', paddingLeft: 18, fontSize: 12, color: '#475569' }}>
            {lines.slice(0, 6).map(l => <li key={l.id}>{l.product_name}</li>)}
            {lines.length > 6 && <li style={{ color: '#94a3b8' }}>+{lines.length - 6} more</li>}
          </ul>
        )}
        <div style={{ marginTop: 10, padding: '8px 12px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 6, fontSize: 12, color: '#991b1b' }}>
          This rejects the <strong>whole delivery</strong> and notifies HQ. No stock change.
          If only part of it is wrong, confirm what arrived and use a short receipt or a credit note instead.
        </div>
        <div style={{ marginTop: 14 }}>
          <label style={lbl}>Reason (required)</label>
          <input type="text" autoFocus value={reason}
            onChange={e => setState(s => ({ ...s, reason: e.target.value }))}
            placeholder="e.g. wrong branch / wrong product / supplier dispute" style={inp} />
        </div>
        <div style={{ display: 'flex', gap: 10, marginTop: 18 }}>
          <button onClick={onCancel} disabled={submitting}
            style={{ flex: 1, padding: '12px', background: '#f1f5f9', color: '#475569', border: '1px solid #e2e8f0', borderRadius: 8, cursor: 'pointer', fontWeight: 600 }}>
            Cancel
          </button>
          <button onClick={onSubmit} disabled={submitting || !(reason || '').trim()}
            style={{ flex: 1, padding: '12px',
                     background: submitting ? '#94a3b8' : '#dc2626',
                     color: '#fff', border: 'none', borderRadius: 8,
                     cursor: submitting ? 'wait' : 'pointer', fontWeight: 700 }}>
            {submitting ? 'Rejecting…' : 'Reject Line'}
          </button>
        </div>
      </div>
    </div>
  );
}

// v1.9.7 — old ReceiveModal removed. Branches now Accept (which navigates
// to the GRN form pre-filled with PO#) or Reject (RejectModal above).
// The inline qty/reason submit path is gone.

const overlay = { position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200, padding: 16 };
const lbl = { display: 'block', fontSize: 11, color: '#64748b', fontWeight: 600, marginBottom: 4 };
const inp = { width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14, background: '#fff', boxSizing: 'border-box' };
const th  = { padding: '10px 12px', fontSize: 11, color: '#64748b', fontWeight: 700, textAlign: 'left', textTransform: 'uppercase', letterSpacing: 0.5 };
const td  = { padding: '8px 12px', color: '#0f172a' };
const tag = (bg, fg) => ({ background: bg, color: fg, fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 10, textTransform: 'uppercase', letterSpacing: 0.4, display: 'inline-flex', alignItems: 'center', gap: 4 });
